import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLayout from '../../components/layout/AdminLayout';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import {
  Calendar, Clock, Users, Mail, MessageSquare, Plus, X, Send, History, ChevronRight, ChevronDown, Megaphone, FileText, Pencil, Trash2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRESET_OFFSETS = [
  { label: '15 minutes before', value: 15 },
  { label: '1 hour before', value: 60 },
  { label: '2 hours before', value: 120 },
  { label: '6 hours before', value: 360 },
  { label: '1 day before', value: 1440 },
  { label: '2 days before', value: 2880 },
  { label: '1 week before', value: 10080 },
];

function offsetLabel(min) {
  if (min >= 1440 && min % 1440 === 0) {
    const d = min / 1440;
    return `${d} day${d > 1 ? 's' : ''} before`;
  }
  if (min >= 60 && min % 60 === 0) {
    const h = min / 60;
    return `${h} hour${h > 1 ? 's' : ''} before`;
  }
  return `${min} minutes before`;
}

export default function AdminEventReminders() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [allBlastOpen, setAllBlastOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/events/list`, { withCredentials: true });
      setEvents(res.data.events || []);
    } catch (e) {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  return (
    <AdminLayout title="Event Reminders & Blasts">
      <div data-testid="admin-event-reminders-page">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="heading-2 text-2xl text-[#0A0F1C]" data-testid="event-reminders-title">
              Event Reminders & Blasts ({events.length})
            </h1>
            <p className="text-xs text-[#475569] mt-1">
              Auto-reminders run every 5 min. Email always delivers; SMS requires a registrant phone number and live Twilio.
            </p>
          </div>
          <button
            onClick={() => setTemplatesOpen(true)}
            className="inline-flex items-center gap-2 bg-white border border-[#E2E8F0] hover:bg-[#F9FAFB] text-[#475569] px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            data-testid="manage-templates-btn"
          >
            <FileText size={16} />
            Templates
          </button>
          <button
            onClick={() => setAllBlastOpen(true)}
            className="inline-flex items-center gap-2 bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            data-testid="blast-all-btn"
          >
            <Megaphone size={16} />
            Blast All Upcoming
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-10 h-10 border-4 border-[#1D4ED8] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : events.length === 0 ? (
          <div className="bg-white border border-[#E2E8F0] rounded-lg p-8 text-center">
            <Calendar size={48} className="text-[#E2E8F0] mx-auto mb-4" />
            <p className="text-[#475569]">No upcoming events.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {events.map((e, idx) => (
              <EventRow
                key={e.event_id}
                event={e}
                index={idx}
                expanded={expandedId === e.event_id}
                onToggle={() => setExpandedId(expandedId === e.event_id ? null : e.event_id)}
                onChange={fetchEvents}
              />
            ))}
          </div>
        )}

        {allBlastOpen && (
          <BlastAllModal onClose={() => setAllBlastOpen(false)} />
        )}
        {templatesOpen && (
          <TemplatesManagerModal onClose={() => setTemplatesOpen(false)} />
        )}
      </div>
    </AdminLayout>
  );
}

function EventRow({ event, index, expanded, onToggle, onChange }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid={`event-row-${index}`}>
      <button
        onClick={onToggle}
        className="w-full px-5 py-4 flex items-center justify-between hover:bg-[#F9FAFB]"
        data-testid={`event-toggle-${index}`}
      >
        <div className="flex items-center gap-4 text-left">
          <div className="w-12 h-12 bg-[#0F172A] text-white rounded-lg flex flex-col items-center justify-center">
            <span className="text-[10px] uppercase">
              {new Date(event.event_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short' })}
            </span>
            <span className="text-base font-bold leading-none">
              {new Date(event.event_date + 'T00:00:00').getDate()}
            </span>
          </div>
          <div>
            <div className="font-bold text-[#0A0F1C] text-sm">{event.title}</div>
            <div className="text-xs text-[#475569] flex items-center gap-3 mt-0.5">
              <span className="flex items-center gap-1"><Clock size={12} />{event.event_time}</span>
              <span className="flex items-center gap-1"><Users size={12} />{event.registered_count} registered</span>
              <span className="flex items-center gap-1 text-[#1D4ED8] font-medium">
                {event.reminder_schedule?.length || 0} reminders
              </span>
            </div>
          </div>
        </div>
        {expanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
      </button>

      {expanded && (
        <div className="border-t border-[#E2E8F0] p-5 bg-[#FAFAFA]" data-testid={`event-detail-${index}`}>
          <EventDetail event={event} onChange={onChange} indexSuffix={index} />
        </div>
      )}
    </div>
  );
}

function EventDetail({ event, onChange, indexSuffix }) {
  const [activeTab, setActiveTab] = useState('reminders');
  const tabs = [
    { key: 'reminders', label: 'Reminders', icon: Clock },
    { key: 'registrants', label: 'Registrants', icon: Users },
    { key: 'blast', label: 'Send Blast', icon: Megaphone },
    { key: 'history', label: 'History', icon: History },
  ];
  return (
    <div>
      <div className="flex items-center gap-1 mb-5 border-b border-[#E2E8F0]">
        {tabs.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors ${
                activeTab === t.key
                  ? 'border-[#1D4ED8] text-[#1D4ED8]'
                  : 'border-transparent text-[#94A3B8] hover:text-[#475569]'
              }`}
              data-testid={`tab-${t.key}-${indexSuffix}`}
            >
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>
      {activeTab === 'reminders' && <RemindersEditor event={event} onChange={onChange} indexSuffix={indexSuffix} />}
      {activeTab === 'registrants' && <RegistrantsList eventId={event.event_id} indexSuffix={indexSuffix} />}
      {activeTab === 'blast' && <BlastComposer event={event} indexSuffix={indexSuffix} />}
      {activeTab === 'history' && <ReminderHistory eventId={event.event_id} indexSuffix={indexSuffix} />}
    </div>
  );
}

function RemindersEditor({ event, onChange, indexSuffix }) {
  const [rules, setRules] = useState(event.reminder_schedule || []);
  const [saving, setSaving] = useState(false);

  const addRule = (offset) => {
    if (rules.some((r) => r.offset_minutes_before === offset)) {
      toast.info('That reminder offset is already in the schedule');
      return;
    }
    setRules([...rules, { offset_minutes_before: offset, channels: ['email', 'sms'] }].sort((a, b) => b.offset_minutes_before - a.offset_minutes_before));
  };

  const removeRule = (idx) => setRules(rules.filter((_, i) => i !== idx));

  const toggleChannel = (idx, channel) => {
    const next = [...rules];
    const has = next[idx].channels.includes(channel);
    next[idx].channels = has ? next[idx].channels.filter((c) => c !== channel) : [...next[idx].channels, channel];
    setRules(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      const valid = rules.filter((r) => r.channels.length > 0);
      await axios.patch(
        `${API_URL}/api/admin/events/${event.event_id}/reminder-schedule`,
        { rules: valid },
        { withCredentials: true }
      );
      toast.success('Reminder schedule saved');
      onChange();
    } catch (e) {
      toast.error('Failed to save schedule');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid={`reminders-editor-${indexSuffix}`}>
      <p className="text-xs text-[#475569] mb-3">
        Reminders fire automatically. Email always delivers via Resend; SMS only sends when the registrant provided a phone number.
      </p>
      <div className="space-y-2 mb-4">
        {rules.length === 0 && (
          <div className="text-xs text-[#94A3B8] italic">No reminders scheduled. Add one below.</div>
        )}
        {rules.map((r, idx) => (
          <div key={idx} className="bg-white border border-[#E2E8F0] rounded-lg p-3 flex items-center justify-between" data-testid={`reminder-rule-${indexSuffix}-${idx}`}>
            <div className="text-sm font-medium text-[#0A0F1C]">{offsetLabel(r.offset_minutes_before)}</div>
            <div className="flex items-center gap-3">
              <label className="text-xs flex items-center gap-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={r.channels.includes('email')}
                  onChange={() => toggleChannel(idx, 'email')}
                  data-testid={`chan-email-${indexSuffix}-${idx}`}
                />
                <Mail size={12} /> Email
              </label>
              <label className="text-xs flex items-center gap-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={r.channels.includes('sms')}
                  onChange={() => toggleChannel(idx, 'sms')}
                  data-testid={`chan-sms-${indexSuffix}-${idx}`}
                />
                <MessageSquare size={12} /> SMS
              </label>
              <button onClick={() => removeRule(idx)} className="text-[#EF4444] hover:text-[#DC2626]" data-testid={`remove-rule-${indexSuffix}-${idx}`}>
                <X size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>
      <div className="bg-white border border-[#E2E8F0] rounded-lg p-3 mb-4">
        <div className="text-xs font-bold text-[#475569] uppercase tracking-wider mb-2">Add reminder</div>
        <div className="flex flex-wrap gap-2">
          {PRESET_OFFSETS.map((p) => (
            <button
              key={p.value}
              onClick={() => addRule(p.value)}
              className="text-xs bg-[#EFF6FF] hover:bg-[#DBEAFE] text-[#1D4ED8] px-3 py-1.5 rounded-full font-medium flex items-center gap-1"
              data-testid={`preset-${p.value}-${indexSuffix}`}
            >
              <Plus size={12} /> {p.label}
            </button>
          ))}
        </div>
      </div>
      <Button
        onClick={save}
        disabled={saving}
        className="btn-primary-blue"
        data-testid={`save-reminders-${indexSuffix}`}
      >
        {saving ? 'Saving...' : 'Save Schedule'}
      </Button>
    </div>
  );
}

function RegistrantsList({ eventId, indexSuffix }) {
  const [regs, setRegs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios
      .get(`${API_URL}/api/admin/events/${eventId}/registrants`, { withCredentials: true })
      .then((res) => setRegs(res.data.registrants || []))
      .catch(() => toast.error('Failed to load registrants'))
      .finally(() => setLoading(false));
  }, [eventId]);
  if (loading) return <div className="text-sm text-[#475569]">Loading...</div>;
  if (!regs.length)
    return (
      <div className="bg-white border border-[#E2E8F0] rounded-lg p-6 text-center text-sm text-[#475569]" data-testid={`no-registrants-${indexSuffix}`}>
        No registrants yet.
      </div>
    );
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid={`registrants-list-${indexSuffix}`}>
      <table className="w-full text-sm">
        <thead className="bg-[#F9FAFB]">
          <tr>
            <th className="text-left px-4 py-2 text-xs font-bold text-[#475569] uppercase">Name</th>
            <th className="text-left px-4 py-2 text-xs font-bold text-[#475569] uppercase">Email</th>
            <th className="text-left px-4 py-2 text-xs font-bold text-[#475569] uppercase">Phone</th>
            <th className="text-left px-4 py-2 text-xs font-bold text-[#475569] uppercase">Registered</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#F1F5F9]">
          {regs.map((r, i) => (
            <tr key={r.registration_id || i}>
              <td className="px-4 py-2">{r.name || `${r.first_name || ''} ${r.last_name || ''}`.trim()}</td>
              <td className="px-4 py-2 text-[#1D4ED8]">{r.email}</td>
              <td className="px-4 py-2">{r.phone || <span className="text-[#94A3B8]">-</span>}</td>
              <td className="px-4 py-2 text-xs text-[#94A3B8]">{new Date(r.registered_at).toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BlastComposer({ event, indexSuffix }) {
  const [channels, setChannels] = useState(['email']);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/blast-templates`, { withCredentials: true })
      .then((res) => setTemplates(res.data.items || []))
      .catch(() => {});
  }, []);

  const applyTemplate = (tplId) => {
    if (!tplId) return;
    const tpl = templates.find((t) => t.template_id === tplId);
    if (!tpl) return;
    setSubject(tpl.subject || '');
    setMessage(tpl.body || '');
    toast.info(`Loaded template: ${tpl.name}`);
  };

  const toggleChannel = (c) => {
    setChannels(channels.includes(c) ? channels.filter((x) => x !== c) : [...channels, c]);
  };

  const send = async () => {
    if (channels.length === 0) return toast.error('Pick at least one channel');
    if (!message.trim()) return toast.error('Message is required');
    if (!window.confirm(`Send this blast to all ${event.registered_count} registrants?`)) return;
    setSending(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/events/${event.event_id}/blast`,
        { channels, subject: subject.trim() || undefined, message },
        { withCredentials: true }
      );
      const s = res.data.stats || {};
      toast.success(`Blast sent: ${s.email || 0} email, ${s.sms || 0} SMS, ${s.skipped || 0} skipped`);
      setSubject('');
      setMessage('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send blast');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-3" data-testid={`blast-composer-${indexSuffix}`}>
      {templates.length > 0 && (
        <select
          onChange={(e) => applyTemplate(e.target.value)}
          defaultValue=""
          className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm bg-white"
          data-testid={`blast-template-picker-${indexSuffix}`}
        >
          <option value="">Pick a template... (optional)</option>
          {templates.map((t) => (
            <option key={t.template_id} value={t.template_id}>
              {t.name}{t.tag ? ` (${t.tag})` : ''}
            </option>
          ))}
        </select>
      )}
      <div className="flex gap-3">
        <label className="text-sm flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={channels.includes('email')} onChange={() => toggleChannel('email')} data-testid={`blast-email-${indexSuffix}`} />
          <Mail size={14} /> Email
        </label>
        <label className="text-sm flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={channels.includes('sms')} onChange={() => toggleChannel('sms')} data-testid={`blast-sms-${indexSuffix}`} />
          <MessageSquare size={14} /> SMS
        </label>
      </div>
      {channels.includes('email') && (
        <Input
          placeholder="Email subject (optional)"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          data-testid={`blast-subject-${indexSuffix}`}
        />
      )}
      <textarea
        placeholder="Type your message... (newlines preserved). For SMS keep it short."
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        rows={5}
        className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm"
        data-testid={`blast-message-${indexSuffix}`}
      />
      <div className="flex items-center justify-between">
        <span className="text-xs text-[#94A3B8]">{message.length} chars{channels.includes('sms') && message.length > 160 ? ' (will be split)' : ''}</span>
        <Button onClick={send} disabled={sending} className="btn-primary-orange flex items-center gap-2" data-testid={`blast-send-${indexSuffix}`}>
          <Send size={14} /> {sending ? 'Sending...' : `Send to ${event.registered_count} registrants`}
        </Button>
      </div>
    </div>
  );
}

function ReminderHistory({ eventId, indexSuffix }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios
      .get(`${API_URL}/api/admin/events/${eventId}/reminder-history`, { withCredentials: true })
      .then((res) => setItems(res.data.items || []))
      .catch(() => toast.error('Failed to load history'))
      .finally(() => setLoading(false));
  }, [eventId]);
  if (loading) return <div className="text-sm text-[#475569]">Loading...</div>;
  if (!items.length)
    return (
      <div className="bg-white border border-[#E2E8F0] rounded-lg p-6 text-center text-sm text-[#475569]" data-testid={`history-empty-${indexSuffix}`}>
        No reminders have fired yet.
      </div>
    );
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid={`history-list-${indexSuffix}`}>
      <table className="w-full text-sm">
        <thead className="bg-[#F9FAFB]">
          <tr>
            <th className="text-left px-4 py-2 text-xs font-bold text-[#475569] uppercase">When</th>
            <th className="text-left px-4 py-2 text-xs font-bold text-[#475569] uppercase">Recipient</th>
            <th className="text-left px-4 py-2 text-xs font-bold text-[#475569] uppercase">Channel</th>
            <th className="text-left px-4 py-2 text-xs font-bold text-[#475569] uppercase">Label</th>
            <th className="text-left px-4 py-2 text-xs font-bold text-[#475569] uppercase">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#F1F5F9]">
          {items.map((r, i) => (
            <tr key={i}>
              <td className="px-4 py-2 text-xs text-[#94A3B8]">{new Date(r.sent_at).toLocaleString()}</td>
              <td className="px-4 py-2 text-[#1D4ED8]">{r.email}</td>
              <td className="px-4 py-2">{r.channel}</td>
              <td className="px-4 py-2">{r.label}</td>
              <td className="px-4 py-2">
                {r.success ? <span className="text-green-700">Sent</span> : <span className="text-[#EF4444]">Skipped</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BlastAllModal({ onClose }) {
  const [channels, setChannels] = useState(['email']);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/blast-templates`, { withCredentials: true })
      .then((res) => setTemplates(res.data.items || []))
      .catch(() => {});
  }, []);

  const applyTemplate = (tplId) => {
    if (!tplId) return;
    const tpl = templates.find((t) => t.template_id === tplId);
    if (!tpl) return;
    setSubject(tpl.subject || '');
    setMessage(tpl.body || '');
    toast.info(`Loaded template: ${tpl.name}`);
  };

  const toggleChannel = (c) => {
    setChannels(channels.includes(c) ? channels.filter((x) => x !== c) : [...channels, c]);
  };

  const send = async () => {
    if (channels.length === 0) return toast.error('Pick at least one channel');
    if (!message.trim()) return toast.error('Message is required');
    if (!window.confirm('Send this blast to ALL registrants across ALL upcoming events?')) return;
    setSending(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/events/blast-all`,
        { channels, subject: subject.trim() || undefined, message },
        { withCredentials: true }
      );
      const s = res.data.stats || {};
      toast.success(`Blast sent to ${res.data.recipient_count} people: ${s.email || 0} email, ${s.sms || 0} SMS`);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send blast');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="blast-all-modal">
      <div className="bg-white rounded-xl max-w-lg w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-[#0A0F1C] flex items-center gap-2">
            <Megaphone size={18} className="text-[#F97316]" />
            Blast All Upcoming Registrants
          </h3>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-[#475569]" data-testid="blast-all-close">
            <X size={20} />
          </button>
        </div>
        <p className="text-xs text-[#475569] mb-4">
          Sends one message to every unique registrant across every upcoming event (deduped by email).
        </p>
        {templates.length > 0 && (
          <select
            onChange={(e) => applyTemplate(e.target.value)}
            defaultValue=""
            className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm bg-white mb-3"
            data-testid="blast-all-template-picker"
          >
            <option value="">Pick a template... (optional)</option>
            {templates.map((t) => (
              <option key={t.template_id} value={t.template_id}>
                {t.name}{t.tag ? ` (${t.tag})` : ''}
              </option>
            ))}
          </select>
        )}
        <div className="flex gap-3 mb-3">
          <label className="text-sm flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={channels.includes('email')} onChange={() => toggleChannel('email')} data-testid="blast-all-chan-email" />
            <Mail size={14} /> Email
          </label>
          <label className="text-sm flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={channels.includes('sms')} onChange={() => toggleChannel('sms')} data-testid="blast-all-chan-sms" />
            <MessageSquare size={14} /> SMS
          </label>
        </div>
        {channels.includes('email') && (
          <Input
            placeholder="Email subject (optional)"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="mb-3"
            data-testid="blast-all-subject"
          />
        )}
        <textarea
          placeholder="Type your message..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={5}
          className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm mb-3"
          data-testid="blast-all-message"
        />
        <Button
          onClick={send}
          disabled={sending}
          className="btn-primary-orange w-full flex items-center justify-center gap-2"
          data-testid="blast-all-send"
        >
          <Send size={14} /> {sending ? 'Sending...' : 'Send Blast'}
        </Button>
      </div>
    </div>
  );
}


function TemplatesManagerModal({ onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | template_id
  const [form, setForm] = useState({ name: '', subject: '', body: '', channels: ['both'], tag: '' });

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/blast-templates`, { withCredentials: true });
      setItems(res.data.items || []);
    } catch {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => {
    setForm({ name: '', subject: '', body: '', channels: ['both'], tag: '' });
    setEditing('new');
  };

  const startEdit = (t) => {
    setForm({
      name: t.name || '',
      subject: t.subject || '',
      body: t.body || '',
      channels: t.channels || ['both'],
      tag: t.tag || '',
    });
    setEditing(t.template_id);
  };

  const cancelEdit = () => setEditing(null);

  const save = async () => {
    if (!form.name.trim() || !form.body.trim()) {
      toast.error('Name and body are required');
      return;
    }
    try {
      const payload = {
        name: form.name.trim(),
        subject: form.subject.trim() || undefined,
        body: form.body,
        channels: form.channels.length ? form.channels : ['both'],
        tag: form.tag.trim() || undefined,
      };
      if (editing === 'new') {
        await axios.post(`${API_URL}/api/admin/blast-templates`, payload, { withCredentials: true });
        toast.success('Template created');
      } else {
        await axios.patch(`${API_URL}/api/admin/blast-templates/${editing}`, payload, { withCredentials: true });
        toast.success('Template updated');
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save template');
    }
  };

  const del = async (tplId) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/blast-templates/${tplId}`, { withCredentials: true });
      toast.success('Template deleted');
      load();
    } catch {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="templates-manager-modal">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-[#0A0F1C] flex items-center gap-2">
            <FileText size={18} className="text-[#1D4ED8]" />
            Blast Templates
          </h3>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-[#475569]" data-testid="templates-close">
            <X size={20} />
          </button>
        </div>

        {editing ? (
          <div className="space-y-3" data-testid="template-edit-form">
            <Input
              placeholder="Template name (e.g. Last-minute reminder)"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="tpl-name"
            />
            <Input
              placeholder="Email subject (optional)"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              data-testid="tpl-subject"
            />
            <textarea
              placeholder="Body - keep it concise for SMS compatibility"
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              rows={5}
              className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm"
              data-testid="tpl-body"
            />
            <Input
              placeholder="Tag (optional, e.g. reminder, promo, cancel)"
              value={form.tag}
              onChange={(e) => setForm({ ...form, tag: e.target.value })}
              data-testid="tpl-tag"
            />
            <div className="flex gap-3">
              {['email', 'sms', 'both'].map((c) => (
                <label key={c} className="text-sm flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.channels.includes(c)}
                    onChange={() =>
                      setForm({
                        ...form,
                        channels: form.channels.includes(c)
                          ? form.channels.filter((x) => x !== c)
                          : [...form.channels, c],
                      })
                    }
                    data-testid={`tpl-chan-${c}`}
                  />
                  {c.toUpperCase()}
                </label>
              ))}
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={cancelEdit} className="btn-outline flex-1" data-testid="tpl-cancel">Cancel</Button>
              <Button onClick={save} className="btn-primary-blue flex-1" data-testid="tpl-save">Save Template</Button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex justify-end mb-3">
              <Button onClick={startNew} className="btn-primary-blue flex items-center gap-2" data-testid="tpl-new">
                <Plus size={14} /> New Template
              </Button>
            </div>
            {loading ? (
              <div className="text-sm text-[#475569] py-6 text-center">Loading templates...</div>
            ) : items.length === 0 ? (
              <div className="text-sm text-[#475569] py-6 text-center" data-testid="tpl-empty">No templates yet.</div>
            ) : (
              <div className="space-y-2" data-testid="tpl-list">
                {items.map((t) => (
                  <div key={t.template_id} className="border border-[#E2E8F0] rounded-lg p-3" data-testid={`tpl-row-${t.template_id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-bold text-sm text-[#0A0F1C]">{t.name}</div>
                          {t.tag && (
                            <span className="text-[10px] px-2 py-0.5 bg-[#EFF6FF] text-[#1D4ED8] rounded-full font-medium uppercase tracking-wider">
                              {t.tag}
                            </span>
                          )}
                          {t.is_default && (
                            <span className="text-[10px] px-2 py-0.5 bg-[#F1F5F9] text-[#475569] rounded-full font-medium uppercase tracking-wider">
                              default
                            </span>
                          )}
                        </div>
                        {t.subject && <div className="text-xs text-[#475569] mt-1 truncate">Subject: {t.subject}</div>}
                        <div className="text-xs text-[#94A3B8] mt-1 line-clamp-2">{t.body}</div>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <button onClick={() => startEdit(t)} className="p-1.5 text-[#1D4ED8] hover:bg-[#EFF6FF] rounded" data-testid={`tpl-edit-${t.template_id}`}>
                          <Pencil size={14} />
                        </button>
                        <button onClick={() => del(t.template_id)} className="p-1.5 text-[#EF4444] hover:bg-red-50 rounded" data-testid={`tpl-delete-${t.template_id}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
